import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { nanoid } from "nanoid";
import dayjs from "dayjs";
import { t, i18n } from "@/locales/i18n";

export const useUiStore = defineStore("ui", () => {
  const isDataLoaded = ref(false);
  const isPreloader = ref(true);
  const isOnboarding = ref(false);
  const isWelcomeScreen = ref(!localStorage.getItem("isOnboardingSkipped"));
  const isToastActive = ref(false);

  const errors = ref([]);
  const toasts = ref([]);

  const getErrorString = (error) => {
    const getI18n = (errorCodeFori18n, altMessage = null) => {
      const translatedMessage = t(`errors|${errorCodeFori18n}`);
      return translatedMessage !== `errors|${errorCodeFori18n}`
        ? translatedMessage
        : (altMessage ?? errorCodeFori18n);
    };

    if (error.message && error.code) {
      return getI18n(error.code, `${error.message} (${error.code})`);
    }

    if (typeof error.error === "string") {
      return getI18n(error.error);
    }

    if (error.error && error.error.code) {
      return getI18n(error.error.code);
    }

    if (error.required) {
      return false;
    }
    return t("errors|unknown");
  };

  const addError = (error) => {
    let message = getErrorString(error);

    if (message) {
      const obj = {
        id: nanoid(),
        message,
      };

      errors.value.push(obj);
      setTimeout(() => {
        errors.value = errors.value.filter((a) => a.id !== obj.id);
      }, 3000);
    }
  };

  const notifications = ref([]);
  const addNotification = (type, message) => {
    const obj = {
      id: nanoid(),
      type,
      message,
    };
    notifications.value.push(obj);
    setTimeout(() => {
      notifications.value = notifications.value.filter((a) => a.id !== obj.id);
    }, 3000);
  };

  const addToast = (message, options = {}) => {
    const durationMs =
      typeof options.duration === "number" ? options.duration : 3000;
    const type = options.type ?? "default";
    const toast = {
      id: nanoid(),
      type,
      message,
    };
    toasts.value.push(toast);
    isToastActive.value = true;
    setTimeout(() => {
      toasts.value = toasts.value.filter((t) => t.id !== toast.id);
      if (toasts.value.length === 0) {
        isToastActive.value = false;
      }
    }, durationMs);
  };

  const showPreloader = () => {
    isPreloader.value = true;
  };

  const hidePreloader = () => {
    isPreloader.value = false;
  };

  const setDataLoaded = (value) => {
    isDataLoaded.value = value;
  };

  const setIsOnboarding = (value) => {
    isOnboarding.value = value;
  };

  const setIsWelcomeScreen = (value) => {
    isWelcomeScreen.value = value;
  };

  const setIsToastActive = (value) => {
    isToastActive.value = value;
  };

  const getIsWelcomeScreen = () => {
    return isWelcomeScreen.value;
  };

  const getIsOnboarding = () => {
    return isOnboarding.value;
  };

  const getIsToastActive = () => {
    return isToastActive.value;
  };

  const language = ref("en");
  const locale = computed(() =>
    language.value === "en" ? "en-US" : language.value,
  );
  const languages = computed(() => [
    {
      title: t("profile|russian"),
      value: "ru",
    },
    {
      title: t("profile|english"),
      value: "en",
    },
  ]);

  const getLanguage = () => {
    // Язык в localStorage (в TMA-webview cookie режутся). Если ключа нет —
    // язык подтянет серверный запрос в getInitData (loadLanguage).
    const lang = localStorage.getItem("language");

    if (lang) {
      language.value = lang;
    }
    dayjs.locale(language.value);
    i18n.locale.value = language.value;
  };

  const changeLanguage = (lang) => {
    language.value = lang;
    i18n.locale.value = lang;
    dayjs.locale(lang);
    localStorage.setItem("language", language.value);
  };

  return {
    isPreloader,
    showPreloader,
    hidePreloader,
    isDataLoaded,
    setDataLoaded,
    addError,
    errors,
    notifications,
    addNotification,
    language,
    locale,
    languages,
    changeLanguage,
    getLanguage,
    setIsOnboarding,
    getIsOnboarding,
    setIsWelcomeScreen,
    getIsWelcomeScreen,
    setIsToastActive,
    getIsToastActive,
    toasts,
    addToast,
  };
});
